// Tela de Backlog (ticket #27, ADR-0007; ADR-0019 para o Backlog Inicial;
// ADR-0022 para a Perda) — o "chiqueirinho" de todos os Clientes. Reescrita
// nesta sessão em 3 seções: "Precisa de atenção" (o que provavelmente virou
// perda, ou tem cadastro incompleto), a matriz de saldo Tipo de Peça × Cliente
// (mesmo padrão técnico de `_matriz-precos.ts`), e o "Histórico do backlog" —
// uma tabela única que junta o que está aberto com o que já saiu (de carona
// numa Devolução seguinte, ou por uma Perda), cada saída com sua própria
// data. Duas saídas possíveis: a baixa automática na Devolução de um Lote
// seguinte, e a Perda registrada aqui à mão.

import {
  codigoSublote,
  historicoBacklog,
  MOTIVO_BACKLOG_INICIAL,
  type LinhaHistorico,
  type SaldoLinha,
} from "../../domain/backlog.ts";
import { formatData, hojeISO } from "../../domain/datas.ts";
import type { Snapshot } from "../../domain/snapshot.ts";
import { icon } from "../icons.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, type Coluna, botao } from "../ui.ts";
import { abrirBacklogInicial } from "./_backlog-inicial-form.ts";
import { abrirConfirmacao, barraFiltros, filtroData, telaLista } from "./_cadastro.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo } from "./_lista-filtros.ts";
import { abrirPerda } from "./_perda-form.ts";
import { podeEditarModulo } from "./_permissao.ts";

/** Dias corridos entre duas datas ISO (nunca negativo). */
function diasEntre(desde: string, ate: string): number {
  const [y1, m1, d1] = desde.split("-").map(Number);
  const [y2, m2, d2] = ate.split("-").map(Number);
  const de = new Date(y1 ?? 1970, (m1 ?? 1) - 1, d1 ?? 1);
  const a = new Date(y2 ?? 1970, (m2 ?? 1) - 1, d2 ?? 1);
  return Math.max(0, Math.round((a.getTime() - de.getTime()) / 86_400_000));
}

/** Dias corridos entre uma data ISO e hoje. */
function diasParado(desde: string): number {
  return diasEntre(desde, hojeISO());
}

interface AgregadoMatriz {
  total: number;
  /** Backlog Inicial deste par é negativo — sinal de cadastro incompleto,
   * mesmo que o total dê positivo por causa de Sublotes reais (ADR-0019). */
  inicialNegativo: boolean;
}

/** Soma o saldo aberto por (Tipo, Cliente) — a célula da matriz. */
function agregarMatriz(linhas: readonly SaldoLinha[]): Map<string, AgregadoMatriz> {
  const m = new Map<string, AgregadoMatriz>();
  for (const l of linhas) {
    const k = `${l.tipoId}|${l.clienteId}`;
    const cur = m.get(k) ?? { total: 0, inicialNegativo: false };
    cur.total += l.quantidade;
    if (l.origemLoteId === null && l.quantidade < 0) cur.inicialNegativo = true;
    m.set(k, cur);
  }
  return m;
}

const SITUACOES = [
  { id: "aberto", nome: "No Backlog" },
  { id: "entrada", nome: "Entrou" },
  { id: "devolvido", nome: "Devolvido" },
  { id: "retida", nome: "Retida de Novo" },
  { id: "perda", nome: "Perda" },
] as const;

const PILL_SITUACAO: Record<LinhaHistorico["situacao"], { classe: string; texto: string }> = {
  aberto: { classe: "pill-backlog-aberto", texto: "No Backlog" },
  // ADR-0030: o cadastro do Backlog Inicial ou as retidas de uma OS entrando
  entrada: { classe: "pill-backlog-entrada", texto: "Entrou" },
  devolvido: { classe: "pill-backlog-devolvido", texto: "Devolvido" },
  retida: { classe: "pill-backlog-retida", texto: "Retida de Novo" },
  perda: { classe: "pill-backlog-perda", texto: "Perda" },
};

export function renderBacklog(container: HTMLElement, store: Store): () => void {
  // Duas listas, cada uma com o seu estado (#93): a matriz ("quanto tem
  // hoje") e o Histórico ("o que entrou e saiu") — perguntas diferentes,
  // filtros independentes. null = todos.
  const matriz = new EstadoLista(
    {
      cliente: null as string[] | null,
      categoria: null as string[] | null,
      tipo: null as string[] | null,
      soComBacklogTipo: true,
      soComBacklogCliente: true,
    },
    { ordCol: 1 }, // Categoria ▲ — mesmo padrão de _matriz-precos.ts
  );
  const historico = new EstadoLista({
    cliente: null as string[] | null,
    tipo: null as string[] | null,
    motivo: null as string[] | null,
    situacao: null as string[] | null,
    entrouDe: "",
    entrouAte: "",
    saiuDe: "",
    saiuAte: "",
  });
  const buscaHist = buscaDaLista(historico, "Buscar por cliente, peça, motivo ou sublote…", () => render());

  // "Precisa de Atenção" começa recolhido; a escolha vale enquanto a tela
  // estiver aberta (a tela redesenha a cada ação, ex.: Registrar Perda).
  let atencaoAberta = false;

  const fCliente = filtroMultiplo(matriz, "cliente", "Cliente", () => render());
  const fCategoria = filtroMultiplo(matriz, "categoria", "Categoria", () => render());
  const fTipo = filtroMultiplo(matriz, "tipo", "Tipo de Peça", () => render());
  const fClienteHist = filtroMultiplo(historico, "cliente", "Cliente", () => render());
  const fTipoHist = filtroMultiplo(historico, "tipo", "Tipo de Peça", () => render());
  const fMotivo = filtroMultiplo(historico, "motivo", "Motivo", () => render());
  const fSituacao = filtroMultiplo(historico, "situacao", "Situação", () => render());

  // Mesmo achado de performance do resto da tela: `saldoBacklog`/`historicoBacklog`
  // só recalculam quando o Snapshot muda de referência de verdade.
  let cacheSnap: Snapshot | null = null;
  let cacheAberto: readonly SaldoLinha[] = [];
  let cacheHistorico: readonly LinhaHistorico[] = [];

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "operacional");
    if (snap !== cacheSnap) {
      cacheSnap = snap;
      cacheHistorico = historicoBacklog(snap);
      // As linhas "aberto" do histórico SÃO as mesmas de `saldoBacklog` (é
      // dali que `historicoBacklog` as constrói, campo a campo) — reusar em
      // vez de rodar a mesma varredura de retenções/baixas duas vezes.
      cacheAberto = cacheHistorico.filter((l) => l.situacao === "aberto");
    }

    const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));
    const tipoNome = new Map(snap.tiposPeca.map((t) => [t.id, t.nome]));
    const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
    const tipoPorId = new Map(snap.tiposPeca.map((t) => [t.id, t]));
    const lotePorId = new Map(snap.lotes.map((l) => [l.id, l]));

    const clientesAtivos = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    const tiposAtivos = [...snap.tiposPeca]
      .filter((t) => t.ativo)
      .sort(
        (a, b) =>
          (catNome.get(a.categoriaId) ?? "").localeCompare(catNome.get(b.categoriaId) ?? "", "pt-BR") ||
          a.nome.localeCompare(b.nome, "pt-BR"),
      );
    const motivosPresentes = [...new Set(cacheHistorico.map((l) => l.motivo))].sort((a, b) =>
      a.localeCompare(b, "pt-BR"),
    );
    // Um Tipo, um Cliente ou uma Categoria (por tabela dos Tipos dela) pode
    // ficar sem nenhum item ativo depois de já ter histórico (só não com
    // saldo ABERTO — `definirAtivoTipoPeca`/`definirAtivoCliente` recusam
    // isso) — sem essa união, o filtro escondia pra sempre as linhas
    // "devolvido"/"perda" dele, porque um item inativo nunca entrava no
    // universo do multiSelect pra começo de conversa (`todosPorPadrao` só
    // marca o que foi passado a ele).
    const tipoIdsComHistorico = new Set(cacheHistorico.map((l) => l.tipoId));
    const tiposParaFiltro = [...snap.tiposPeca]
      .filter((t) => t.ativo || tipoIdsComHistorico.has(t.id))
      .sort(
        (a, b) =>
          (catNome.get(a.categoriaId) ?? "").localeCompare(catNome.get(b.categoriaId) ?? "", "pt-BR") ||
          a.nome.localeCompare(b.nome, "pt-BR"),
      );
    const clienteIdsComHistorico = new Set(cacheHistorico.map((l) => l.clienteId));
    const clientesParaFiltro = [...snap.clientes]
      .filter((c) => c.ativo || clienteIdsComHistorico.has(c.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    const categoriaIdsComHistorico = new Set(
      cacheHistorico
        .map((l) => tipoPorId.get(l.tipoId)?.categoriaId)
        .filter((id): id is string => id != null),
    );
    const categoriasParaFiltro = [...snap.categorias]
      .filter(
        (c) =>
          snap.tiposPeca.some((t) => t.ativo && t.categoriaId === c.id) ||
          categoriaIdsComHistorico.has(c.id),
      )
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    fCliente.atualizar(clientesParaFiltro.map((c) => ({ id: c.id, nome: c.nome })));
    fCategoria.atualizar(categoriasParaFiltro.map((c) => ({ id: c.id, nome: c.nome })));
    fTipo.atualizar(tiposParaFiltro.map((t) => ({ id: t.id, nome: t.nome })));
    fMotivo.atualizar(motivosPresentes.map((m) => ({ id: m, nome: m })));
    fSituacao.atualizar(SITUACOES.map((s) => ({ id: s.id, nome: s.nome })));
    fClienteHist.atualizar(clientesParaFiltro.map((c) => ({ id: c.id, nome: c.nome })));
    fTipoHist.atualizar(tiposParaFiltro.map((t) => ({ id: t.id, nome: t.nome })));

    const clienteIds = fCliente.selecionados();
    const categoriaIds = fCategoria.selecionados();
    const tipoIds = fTipo.selecionados();
    const motivoIds = fMotivo.selecionados();
    const situacaoIds = fSituacao.selecionados();
    const clienteHistIds = fClienteHist.selecionados();
    const tipoHistIds = fTipoHist.selecionados();
    const soComBacklogTipo = matriz.filtro("soComBacklogTipo");
    const soComBacklogCliente = matriz.filtro("soComBacklogCliente");
    const entrouDe = historico.filtro("entrouDe");
    const entrouAte = historico.filtro("entrouAte");
    const saiuDe = historico.filtro("saiuDe");
    const saiuAte = historico.filtro("saiuAte");

    const clientesVisiveis = clientesAtivos.filter((c) => clienteIds.size === 0 || clienteIds.has(c.id));
    const tiposVisiveis = tiposAtivos.filter(
      (t) =>
        (tipoIds.size === 0 || tipoIds.has(t.id)) &&
        (categoriaIds.size === 0 || categoriaIds.has(t.categoriaId)),
    );

    // --- "Precisa de atenção": Backlog Inicial negativo, ou Sublote real
    // parado ≥15 dias (o mesmo threshold que `.prazo-vencido` já usa) ---
    const incompletos = cacheAberto
      .filter((l) => l.origemLoteId === null && l.quantidade < 0)
      .sort((a, b) => a.quantidade - b.quantidade);
    const envelhecidos = cacheAberto
      .filter((l): l is SaldoLinha & { origemLoteId: string } => l.origemLoteId !== null && l.quantidade > 0)
      .map((l) => ({ l, dias: diasParado(lotePorId.get(l.origemLoteId)!.coletaData) }))
      .filter(({ dias }) => dias >= 15)
      .sort((a, b) => b.dias - a.dias);

    const atencaoEl =
      incompletos.length === 0 && envelhecidos.length === 0
        ? null
        : el(
            "details",
            { class: "card backlog-atencao", ...(atencaoAberta ? { open: "" } : {}) },
            el(
              "summary",
              { class: "backlog-atencao-cab" },
              el("h2", {}, "Precisa de Atenção"),
              el(
                "span",
                { class: "backlog-atencao-contagem" },
                String(incompletos.length + envelhecidos.length),
              ),
            ),
            el(
              "p",
              { class: "muted" },
              "Peças que provavelmente não vão voltar, ou saldo inicial que falta completar.",
            ),
            ...incompletos.map((l) => {
              const btn = botao("secundario", {}, "Corrigir Saldo Inicial");
              btn.addEventListener("click", () => abrirBacklogInicial(store, render, l.clienteId));
              return el(
                "div",
                { class: "backlog-atencao-item" },
                icon("alerta", 18, { stroke: "var(--danger)" }),
                el(
                  "div",
                  { class: "backlog-atencao-txt" },
                  el("b", {}, `${cliNome.get(l.clienteId) ?? "—"} · ${tipoNome.get(l.tipoId) ?? "—"}`),
                  el(
                    "span",
                    { class: "muted" },
                    `Saldo inicial incompleto — faltam ${Math.abs(l.quantidade)} peça(s) no cadastro`,
                  ),
                ),
                podeGravar ? btn : null,
              );
            }),
            ...envelhecidos.map(({ l, dias }) => {
              const btn = botao("secundario", {}, "Registrar Perda");
              btn.addEventListener("click", () => abrirPerda(store, l, render));
              return el(
                "div",
                { class: "backlog-atencao-item" },
                icon("alerta", 18, { stroke: "var(--danger)" }),
                el(
                  "div",
                  { class: "backlog-atencao-txt" },
                  el("b", {}, `${cliNome.get(l.clienteId) ?? "—"} · ${tipoNome.get(l.tipoId) ?? "—"}`),
                  el("span", { class: "muted" }, `Parado há ${dias} dia(s) · ${l.quantidade} peça(s) · ${l.motivo}`),
                ),
                podeGravar ? btn : null,
              );
            }),
          );

    atencaoEl?.addEventListener("toggle", () => {
      atencaoAberta = (atencaoEl as HTMLDetailsElement).open;
    });

    // --- "Tudo no backlog": matriz Tipo de Peça × Cliente ---
    const agregado = agregarMatriz(cacheAberto);
    const tiposMatriz = soComBacklogTipo
      ? tiposVisiveis.filter((t) =>
          clientesVisiveis.some((c) => (agregado.get(`${t.id}|${c.id}`)?.total ?? 0) !== 0),
        )
      : tiposVisiveis;
    const clientesMatriz = soComBacklogCliente
      ? clientesVisiveis.filter((c) =>
          tiposVisiveis.some((t) => (agregado.get(`${t.id}|${c.id}`)?.total ?? 0) !== 0),
        )
      : clientesVisiveis;

    const checkTipo = el("input", { type: "checkbox" }) as HTMLInputElement;
    checkTipo.checked = soComBacklogTipo;
    checkTipo.addEventListener("change", () => {
      matriz.mudarFiltro("soComBacklogTipo", checkTipo.checked);
      render();
    });
    const checkCliente = el("input", { type: "checkbox" }) as HTMLInputElement;
    checkCliente.checked = soComBacklogCliente;
    checkCliente.addEventListener("change", () => {
      matriz.mudarFiltro("soComBacklogCliente", checkCliente.checked);
      render();
    });

    const novoBtn = botao("secundario", {}, icon("plus", 14, { peso: 2.3 }), el("span", {}, "Backlog Inicial"));
    novoBtn.addEventListener("click", () => abrirBacklogInicial(store, render));

    const barraMatriz = barraFiltros(
      [
        fCliente.element,
        fCategoria.element,
        fTipo.element,
        el("label", { class: "check inline" }, checkTipo, " Só peças com Backlog"),
        el("label", { class: "check inline" }, checkCliente, " Só clientes com Backlog"),
      ],
      () => {
        matriz.limpar();
        render();
      },
      podeGravar ? [novoBtn] : [],
    );

    const celulaMatriz = (t: (typeof tiposMatriz)[number], c: (typeof clientesMatriz)[number]): HTMLElement => {
      const info = agregado.get(`${t.id}|${c.id}`);
      const total = info?.total ?? 0;
      if (total === 0) {
        return el("td", { class: "muted num", "data-sort": "0" }, "—");
      }
      return el(
        "td",
        { class: "num", "data-sort": String(total) },
        info?.inicialNegativo
          ? el(
              "span",
              {
                class: "backlog-alerta",
                title: "Backlog inicial deste cliente/tipo está negativo — falta completar o cadastro.",
              },
              icon("alerta", 14, { stroke: "var(--danger)" }),
              ` ${total}`,
            )
          : el("b", {}, String(total)),
      );
    };

    const matrizEl =
      tiposMatriz.length === 0 || clientesMatriz.length === 0
        ? el("p", { class: "muted lista-vazia" }, matriz.mensagemVazio({ nenhum: "Nada no backlog.", filtrado: "Nada no backlog com esses filtros." }))
        : tabelaPaginada(
            "backlog-matriz",
            [
              { t: "Tipo de Peça", w: 200 },
              { t: "Categoria", w: 130 },
              ...clientesMatriz.map((c): Coluna => ({ t: c.nome, w: 130 })),
            ],
            tiposMatriz.map((t) =>
              el(
                "tr",
                {},
                el("td", {}, t.nome),
                el("td", { class: "muted" }, catNome.get(t.categoriaId) ?? "—"),
                ...clientesMatriz.map((c) => celulaMatriz(t, c)),
              ),
            ),
            matriz.tabela,
            render,
          );

    // --- "Histórico do backlog": aberto + devolvido + perda, numa só tabela ---
    // Data de entrada/saída: com o filtro preenchido, a linha sem essa data
    // (Saldo Inicial não tem entrada; o que está No Backlog não saiu) fica de fora.
    const naFaixa = (d: string | null, de: string, ate: string): boolean =>
      (de === "" && ate === "") || (d != null && (de === "" || d >= de) && (ate === "" || d <= ate));
    const historicoFiltrado = cacheHistorico.filter((l) => {
      if (clienteHistIds.size !== 0 && !clienteHistIds.has(l.clienteId)) return false;
      if (tipoHistIds.size !== 0 && !tipoHistIds.has(l.tipoId)) return false;
      if (motivoIds.size !== 0 && !motivoIds.has(l.motivo)) return false;
      if (situacaoIds.size !== 0 && !situacaoIds.has(l.situacao)) return false;
      if (!naFaixa(l.entrouEm, entrouDe, entrouAte)) return false;
      if (!naFaixa(l.saiuEm, saiuDe, saiuAte)) return false;
      return true;
    });
    const historicoVisivel = historicoFiltrado.filter((l) => {
      const origem = l.origemLoteId ? lotePorId.get(l.origemLoteId) : null;
      return buscaHist.casa(
        cliNome.get(l.clienteId),
        tipoNome.get(l.tipoId),
        l.motivo,
        l.motivoSaida,
        origem ? codigoSublote(origem) : MOTIVO_BACKLOG_INICIAL,
      );
    });

    const barraHistorico = barraFiltros(
      [
        fClienteHist.element,
        fTipoHist.element,
        fMotivo.element,
        fSituacao.element,
        filtroData("Entrou De", entrouDe, (v) => { historico.mudarFiltro("entrouDe", v); render(); }),
        filtroData("Entrou Até", entrouAte, (v) => { historico.mudarFiltro("entrouAte", v); render(); }),
        filtroData("Saiu De", saiuDe, (v) => { historico.mudarFiltro("saiuDe", v); render(); }),
        filtroData("Saiu Até", saiuAte, (v) => { historico.mudarFiltro("saiuAte", v); render(); }),
      ],
      () => {
        historico.limpar();
        render();
      },
    );

    // "Eram"/"Ficaram": saldo do Cliente × Tipo no Backlog inteiro antes e
    // depois da saída — vazio nas linhas ainda abertas (não são uma saída).
    const saldoEl = (v: number | null): HTMLElement =>
      v == null
        ? el("td", { class: "muted num", "data-sort": "" }, "—")
        : el("td", { class: v < 0 ? "prazo-vencido num" : "muted num", "data-sort": String(v) }, String(v));

    // Qtd: nas linhas abertas é o saldo; nos movimentos, com sinal — "+"
    // entrou, "−" saiu —, como numa conta corrente
    const qtdEl = (l: LinhaHistorico): HTMLElement => {
      if (l.situacao === "aberto") {
        return el("td", { class: l.quantidade < 0 ? "prazo-vencido num" : "num", "data-sort": String(l.quantidade) }, el("b", {}, String(l.quantidade)));
      }
      const sinal = l.situacao === "entrada" ? 1 : -1;
      return el(
        "td",
        { class: "num", "data-sort": String(sinal * l.quantidade) },
        el("b", {}, `${sinal > 0 ? "+" : "−"}${l.quantidade}`),
      );
    };

    const linhasHistoricoEl = historicoVisivel.map((l) => {
      const origem = l.origemLoteId ? lotePorId.get(l.origemLoteId) : null;

      // "Entrou em" leva junto há quanto tempo a peça está (ou ficou) parada,
      // e "Motivo" leva o código do Sublote — uma coluna a menos cada, para a
      // tabela caber sem rolar para o lado
      const parado = ((): { texto: string; alerta: boolean } | null => {
        if (l.situacao === "aberto") {
          if (!origem) return null;
          const dias = diasParado(origem.coletaData);
          return { texto: dias === 0 ? "parado desde hoje" : `parado há ${dias} d`, alerta: dias >= 15 };
        }
        if (l.situacao === "entrada" || l.entrouEm == null || l.saiuEm == null) return null;
        return { texto: `ficou ${diasEntre(l.entrouEm, l.saiuEm)} d`, alerta: false };
      })();
      const entrouEl = el(
        "td",
        { class: "muted", "data-sort": l.entrouEm ?? "" },
        l.entrouEm ? formatData(l.entrouEm) : "—",
        parado
          ? el("div", { class: `backlog-hist-motivo-saida${parado.alerta ? " prazo-vencido" : ""}` }, parado.texto)
          : null,
      );
      const motivoEl = el(
        "td",
        { class: "muted" },
        l.motivo,
        origem ? el("div", { class: "backlog-hist-motivo-saida" }, codigoSublote(origem)) : null,
      );

      const { classe, texto } = PILL_SITUACAO[l.situacao];
      const situacaoEl = el(
        "td",
        {},
        el("span", { class: `pill-backlog ${classe}` }, texto),
        l.situacao === "perda" && l.motivoSaida
          ? el("div", { class: "muted backlog-hist-motivo-saida" }, l.motivoSaida)
          : null,
        l.situacao === "entrada"
          ? el(
              "div",
              { class: "muted backlog-hist-motivo-saida" },
              origem
                ? `na OS ${origem.os}${l.movimentoEm ? ` · ${formatData(l.movimentoEm).slice(0, 5)}` : ""}`
                : "cadastro inicial",
            )
          : null,
        (l.situacao === "retida" || l.situacao === "devolvido") && l.destinoLoteId
          ? el(
              "div",
              { class: "muted backlog-hist-motivo-saida" },
              `${l.situacao === "retida" ? "na" : "pela"} OS ${lotePorId.get(l.destinoLoteId)?.os ?? "?"}`,
            )
          : null,
      );

      const acaoEl = (() => {
        if (!podeGravar) return el("td", { class: "acoes" });
        if (l.situacao === "devolvido" || l.situacao === "retida" || l.situacao === "entrada") {
          return el("td", { class: "acoes" });
        }
        if (l.situacao === "perda") {
          const btn = botao("perigo", {}, "Excluir");
          btn.addEventListener("click", () => {
            abrirConfirmacao({
              titulo: "Excluir esta Perda?",
              mensagem: `${l.quantidade} peça(s) voltam para o Backlog.`,
              rotuloConfirmar: "Excluir",
              perigo: true,
              aoConfirmar: () =>
                void store.executar(
                  { agregado: "perda", cmd: { tipo: "excluir", id: l.perdaId! } },
                  "Perda excluída.",
                ),
            });
          });
          return el("td", { class: "acoes" }, btn);
        }
        // "aberto"
        const editar =
          l.origemLoteId === null
            ? (() => {
                const btn = botao("secundario", {}, "Editar");
                btn.addEventListener("click", () => abrirBacklogInicial(store, render, l.clienteId));
                return btn;
              })()
            : null;
        const perdaBtn = botao("secundario", {}, "Registrar Perda");
        perdaBtn.disabled = l.quantidade <= 0;
        perdaBtn.addEventListener("click", () => abrirPerda(store, l, render));
        const perdaAcao =
          l.quantidade <= 0
            ? (() => {
                perdaBtn.title = "Sem saldo positivo nesta linha.";
                return el(
                  "span",
                  { class: "backlog-acao-desabilitada" },
                  perdaBtn,
                  el("small", { class: "muted" }, "Sem saldo"),
                );
              })()
            : perdaBtn;
        return el("td", { class: "acoes" }, editar, perdaAcao);
      })();

      return el(
        "tr",
        {},
        el("td", {}, cliNome.get(l.clienteId) ?? "—"),
        el("td", {}, tipoNome.get(l.tipoId) ?? "—"),
        motivoEl,
        saldoEl(l.saldoAntes),
        qtdEl(l),
        saldoEl(l.saldoDepois),
        entrouEl,
        situacaoEl,
        el("td", { class: "muted", "data-sort": l.saiuEm ?? "" }, l.saiuEm ? formatData(l.saiuEm) : "—"),
        acaoEl,
      );
    });

    const historicoEl =
      historicoVisivel.length === 0
        ? el(
            "p",
            { class: "muted lista-vazia" },
            historico.mensagemVazio({
              nenhum: "O Backlog está vazio — nenhuma peça retida, devolvida ou perdida ainda.",
              filtrado: "Nada no histórico com esses filtros.",
            }),
          )
        : tabelaPaginada(
            "backlog-historico",
            [
              { t: "Cliente", w: 140 },
              { t: "Tipo de Peça", w: 120 },
              { t: "Motivo / Sublote", w: 170 },
              { t: "Eram", w: 64 },
              { t: "Qtd", w: 60 },
              { t: "Ficaram", w: 76 },
              { t: "Entrou em", w: 120 },
              { t: "Situação", w: 130 },
              { t: "Saiu em", w: 100 },
              { t: "", ord: false as const },
            ],
            linhasHistoricoEl,
            historico.tabela,
            render,
          );

    container.append(
      telaLista(
        atencaoEl,
        el("h2", { class: "backlog-secao-titulo" }, "Backlog por Cliente"),
        barraMatriz,
        matrizEl,
        el("hr", { class: "backlog-separador" }),
        el("h2", { class: "backlog-secao-titulo" }, "Histórico do Backlog"),
        barraHistorico,
        buscaHist.elemento(),
        historicoEl,
      ),
    );
  };

  render();
  return store.subscribe(render);
}
